
import React, { Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import { trackPageview } from '@cvt/tracking';
import { useScrollTop } from '@cvt/hooks/useScrollTop';
import { BodyLoading } from '@cvt/components/layout/BodyLoading';
import { useRouter } from '@cvt/routing';
import ErrorBoundary from '@cvt/components/ErrorBoundary';

import { AuthContext } from '@modules/Auth/contexts';
import { Authenticated, NotAuthenticated } from '@modules/Auth/components';

import { Login, SignUp, Impersonate, ResetPassword } from '@modules/Auth/views';
import { Users, CreateUser, EditUser, Invite, Profile } from '@modules/Users/views';
import { Companies, CreateCompany } from '@modules/Companies/views';
import { Programs } from '@modules/Programs/views/Programs';
import { ProgramLinkLanding } from '@modules/Programs/views/ProgramLinkLanding';
import { ProgramLayout } from '@modules/Programs/components/ProgramLayout';

import { EditCompany } from './Companies/EditCompany';
import { Onboarding } from './Onboarding/Onboarding';
import { Objectives } from './Objectives/Objectives';
import { Values } from './Values/Values';
import { Stories } from './Stories/Stories';
import { Meetings } from './Meetings/Meetings';
import { DashboardPage } from './Dashboard/DashboardPage';
import { WelcomePage } from './Dashboard/WelcomePage';


const RETURN_URL_KEY = 'stc_return_url';

const RedirectToLogin: React.FC<{ loginPath: string }> = ({ loginPath }) => {
  const location = useLocation();
  const fullPath = location.pathname + location.search;
  sessionStorage.setItem(RETURN_URL_KEY, fullPath);
  return <Navigate to={loginPath}/>;
};

const ReturnUrlRedirect: React.FC<{ fallback: React.ReactNode }> = ({ fallback }) => {
  const returnUrl = sessionStorage.getItem(RETURN_URL_KEY);

  React.useEffect(() => {
    if (returnUrl) {
      sessionStorage.removeItem(RETURN_URL_KEY);
    }
  }, [returnUrl]);

  if (returnUrl) {
    return <Navigate to={returnUrl} replace/>;
  }
  return <>{fallback}</>;
};


export const Root = () => {
  const router = useRouter();
  useScrollTop();
  const { loading } = React.useContext(AuthContext);
  const location = useLocation();

  React.useEffect(() => {
    trackPageview();
  }, [location]);

  if(loading) {
    return <BodyLoading height="100vh"/>;
  }

  return (
    <ErrorBoundary>
      <Suspense fallback={<BodyLoading height="100vh"/>}>
        <NotAuthenticated>
          <Routes>
            <Route path={router.home.path} element={<WelcomePage/>}/>
            <Route path={router.auth.login.path} element={<Login/>}/>
            <Route path={router.auth.resetPassword.path} element={<ResetPassword/>}/>
            <Route path={router.auth.signup.path} element={<SignUp/>}/>
            <Route path={router.auth.impersonateUser.path} element={<Impersonate/>}/>
            <Route path={router.users.invite().path} element={<Invite/>}/>
            <Route path={router.programs.link().path} element={<ProgramLayout><ProgramLinkLanding/></ProgramLayout>}/>
            <Route path="*" element={<RedirectToLogin loginPath={router.auth.login.path}/>}/>
          </Routes>
        </NotAuthenticated>
        <Authenticated>
          <Routes>
            <Route path={router.home.path} element={<DashboardPage/>}/>
            <Route path={router.auth.impersonateUser.path} element={<Impersonate/>}/>
            <Route path={router.users.myAccount.path} element={<Profile/>}/>
            <Route path={router.users.path} element={<Users/>}/>
            <Route path={`${router.stories.path}/*`} element={<Stories/>}/>
            <Route path={`${router.meetings.path}/*`} element={<Meetings/>}/>
            <Route path={`${router.programs.path}/*`} element={<Programs/>}/>

            <Route path={router.users.create.path} element={<CreateUser/>}/>
            <Route path={router.users.edit().path} element={<EditUser/>}/>

            <Route path={router.companies.path} element={<Companies/>}/>
            <Route path={router.companies.create.path} element={<CreateCompany/>}/>
            <Route path={router.companies.edit().path} element={<EditCompany/>}/>

            <Route path={`${router.onboarding.path}/*`} element={<Onboarding/>}/>

            <Route path={`${router.objectives.path}/*`} element={<Objectives/>}/>
            <Route path={`${router.values.path}/*`} element={<Values/>}/>
            <Route path="*" element={<ReturnUrlRedirect fallback={<Navigate to={router.home.path}/>}/>}/>
          </Routes>
        </Authenticated>
      </Suspense>
    </ErrorBoundary>
  );
};
